/**
 * Username Generator Utility
 * Generates username suggestions based on Auth0 user profile data
 */

/**
 * Sanitizes a string to be username-compatible
 * - Removes special characters except underscores and hyphens
 * - Converts to lowercase
 * - Ensures length is between 3-20 characters
 * - Trims leading/trailing special characters
 *
 * @param {string} str - String to sanitize
 * @param {number} maxLength - Maximum length (default 20)
 * @returns {string} Sanitized username string
 */
const sanitizeUsername = (str, maxLength = 20) => {
  if (!str) return "";

  // Remove special characters except underscores and hyphens, convert to lowercase
  let sanitized = str
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "")
    .substring(0, maxLength);

  // Remove leading/trailing special characters
  sanitized = sanitized.replace(/^[_-]+|[_-]+$/g, "");

  // Ensure minimum length of 3
  if (sanitized.length < 3) {
    return "";
  }

  return sanitized;
};

/**
 * Parses name from Auth0 user object
 * Extracts first and last name components
 *
 * @param {string} name - Full name from Auth0
 * @returns {Object} Object with firstName and lastName
 */
const parseName = (name) => {
  if (!name) return { firstName: "", lastName: "" };

  const parts = name.trim().split(/\s+/);

  return {
    firstName: parts[0] || "",
    lastName: parts.length > 1 ? parts[parts.length - 1] : "",
  };
};

/**
 * Extracts username base from email
 * Uses the part before @ symbol
 *
 * @param {string} email - User's email address
 * @returns {string} Email prefix
 */
const getEmailPrefix = (email) => {
  if (!email) return "";

  const prefix = email.split("@")[0];
  // Remove dots and other special chars
  return prefix.replace(/[^a-zA-Z0-9]/g, "");
};

/**
 * Generates a random number suffix
 *
 * @param {number} digits - Number of digits (default 4)
 * @returns {string} Random number string
 */
const generateRandomNumber = (digits = 4) => {
  const min = Math.pow(10, digits - 1);
  const max = Math.pow(10, digits) - 1;
  return Math.floor(Math.random() * (max - min + 1) + min).toString();
};

/**
 * Gets a random MMA-themed suffix
 *
 * @returns {string} MMA-themed suffix
 */
const getMMASuffix = () => {
  const suffixes = ["Fighter", "Champ", "KO", "Warrior", "Beast", "Legend"];
  return suffixes[Math.floor(Math.random() * suffixes.length)];
};

/**
 * Generates username suggestions based on user profile
 * Creates 4 variations using different strategies
 *
 * @param {Object} user - Auth0 user object
 * @param {string} user.name - User's full name
 * @param {string} user.email - User's email
 * @returns {string[]} Array of 4 username suggestions
 */
export const generateUsernameSuggestions = (user) => {
  const suggestions = [];

  // Parse name or use email as fallback
  const { firstName, lastName } = parseName(user?.name);
  const emailPrefix = getEmailPrefix(user?.email);

  // Determine base name to use
  const hasValidName = firstName && firstName.length >= 2;
  const baseName = hasValidName ? firstName : emailPrefix;
  const fullName =
    hasValidName && lastName ? `${firstName}${lastName}` : baseName;

  if (!baseName) {
    // If we have no valid base, return generic suggestions
    return [
      `Fighter${generateRandomNumber()}`,
      `User${generateRandomNumber()}`,
      `Champ${generateRandomNumber()}`,
      `Warrior${generateRandomNumber()}`,
    ];
  }

  // Variation 1: Full name, no spaces (e.g., "JohnDoe")
  const variation1 = sanitizeUsername(fullName);
  if (variation1 && variation1.length >= 3) {
    suggestions.push(variation1);
  }

  // Variation 2: First name + random number (e.g., "John_4729")
  const variation2 = sanitizeUsername(`${baseName}_${generateRandomNumber()}`);
  if (variation2 && variation2.length >= 3) {
    suggestions.push(variation2);
  }

  // Variation 3: Initials + random number (e.g., "JD_8234")
  if (hasValidName && lastName) {
    const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`;
    const variation3 = sanitizeUsername(
      `${initials}_${generateRandomNumber()}`
    );
    if (variation3 && variation3.length >= 3) {
      suggestions.push(variation3);
    }
  } else {
    // Fallback: base name with different number
    const variation3 = sanitizeUsername(`${baseName}${generateRandomNumber()}`);
    if (variation3 && variation3.length >= 3) {
      suggestions.push(variation3);
    }
  }

  // Variation 4: Name + MMA suffix (e.g., "JohnFighter")
  const mmaSuffix = getMMASuffix();
  const variation4 = sanitizeUsername(`${baseName}${mmaSuffix}`);
  if (variation4 && variation4.length >= 3) {
    suggestions.push(variation4);
  }

  // Ensure we have 4 suggestions, add more if needed
  while (suggestions.length < 4) {
    const fallback = sanitizeUsername(`${baseName}${generateRandomNumber()}`);
    if (fallback && fallback.length >= 3 && !suggestions.includes(fallback)) {
      suggestions.push(fallback);
    }
  }

  // Return only first 4 suggestions
  return suggestions.slice(0, 4);
};

/**
 * Makes a username unique by appending a random number
 * Used when a generated suggestion is already taken
 *
 * @param {string} username - Base username
 * @param {number} maxAttempts - Maximum retry attempts (default 3)
 * @returns {string} Username with random number appended
 */
export const makeUsernameUnique = (username, maxAttempts = 3) => {
  if (!username) return `User${generateRandomNumber()}`;

  // Remove any existing number suffix
  const baseUsername = username.replace(/_?\d+$/, "");

  // Append random number
  const uniqueUsername = sanitizeUsername(
    `${baseUsername}_${generateRandomNumber()}`
  );

  return uniqueUsername || `${baseUsername}${generateRandomNumber()}`;
};

export { sanitizeUsername, parseName, getEmailPrefix };
